import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AppComponent} from 'src/app/app.component';
import {StreamReport} from 'src/app/models/stream-report';
import {VideoService} from 'src/app/services/video.service';
import {StorageManagerService} from '../../services/storage-manager.service';
import {BehaviorSubject, fromEvent, interval, Observable, Subject} from 'rxjs';
import {takeUntil, tap} from 'rxjs/operators';
import {SubtitleService} from '../../services/subtitle.service';
import {PlaylistService} from 'src/app/services/playlist.service';

// declare function initSeekPreview();
//
// declare function loadVtt(url);

// declare function unloadVtt();

//declare function timestampClicked();

declare var webOS: any;
declare var shaka: any;
declare var tizen: any;
declare var webapis: any;

@Component({
  selector: 'shaka-player',
  templateUrl: './shaka-player.component.html',
  styleUrls: ['./shaka-player.component.scss']
})

export class ShakaPlayerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('videoPlayer') videoElementRef: ElementRef;
  videoElement: HTMLVideoElement;
  @ViewChild('videoUI') videoUIElementRef: ElementRef;
  videoUIElement: HTMLDivElement;
  @ViewChild('playButton') playButton: ElementRef;

  @ViewChild('playNext') playNext: ElementRef;

  @ViewChild('watchlistButton') watchlistButton!: ElementRef;

  @Input() autoplay: boolean;
  @Input() poster: string;
  @Input() startTimeMs: number;
  @Input() uid: string;
  @Input() url: string;
  @Input() widevineUrl: string;
  @Input() fairplayUrl: string;
  @Input() fpsHlsUrl: string;
  @Input() videoId: number;
  @Input() previewsUrl: string;
  @Input() textTracks: any;
  @Input() title: string;
  @Input() mediaId: string;

  frequency = 10;
  lastReportedTime = 0;
  timeLeft: number;
  player: any;
  pip: boolean = false;
  fluidContainer: boolean = false;
  isPlaying: boolean = true;

  showReportMediaModal: boolean = false;

  endOfVideo = false;
  endOfVideoCounterId;
  endOfVideoTime = 10;
  cancelled = false;
  overlayTimeout: any = undefined;

  nextVideoId: string | undefined;
  endOfVideoImage = '';
  previousVideoId: string | undefined;
  subtitleClass$: Observable<string>;
  captionsSrc: string = 'assets/cc-off.svg';
  addToPlaylistLoading: boolean = false;

  mutationObserver: MutationObserver = undefined;
  private readonly overlayTimeoutDelay = 3000;
  private playFocusTimeout: any = undefined;

  private _shakaControlsContainer: Element = undefined;

  private destroy$ = new Subject<void>();
  captionsOn: boolean = false;

  webOsVersion$: BehaviorSubject<string> = new BehaviorSubject(undefined);

  videoLoaded = false;
  durationWatchedSecondMs: number = 0;
  seeking = false;
  private watchedInterval: Observable<number> = interval(100).pipe(takeUntil(this.destroy$), tap(() => {
    if (this.videoLoaded && !this.seeking && this.isPlaying) {
      this.durationWatchedSecondMs += 100;
    }
  }));

  private get shakaControlsContainer(): Element {
    if (!this._shakaControlsContainer) {
      this._shakaControlsContainer = document.querySelector('.shaka-controls-container');
    }

    return this._shakaControlsContainer;
  }

  constructor(
    private app: AppComponent,
    private router: Router,
    private route: ActivatedRoute,
    private videoService: VideoService,
    private subtitleService: SubtitleService,
    private storage: StorageManagerService,
    private playlistService: PlaylistService) {
    this.subtitleClass$ = this.subtitleService.subtitleClass();
    this.subtitleService.getSubtitleOptions().subscribe();
  }

  ngOnInit() {
    this.watchedInterval.subscribe();

    this.videoService.nextVideoId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(x => {
        this.nextVideoId = x;
      });

    this.videoService.nextVideoImage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(x => {
        this.endOfVideoImage = x;
      });

    this.videoService.previousVideoId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(x => {
        this.previousVideoId = x;
      });
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes.autoplay) {
      this.cancelled = !changes.autoplay.currentValue;
    }
  }

  ngAfterViewInit(): void {

    fromEvent(document, 'tizenhwkey')
      .pipe(takeUntil(this.destroy$))
      .subscribe((e: any) => {
        if (e.keyName === 'back') {
          this.handleBackKey();
        }
      })

    fromEvent(document, 'keyup')
      .pipe(takeUntil(this.destroy$))
      .subscribe((e: KeyboardEvent) => {
        if (e.key === 'Backspace') {
          this.handleBackKey();
        }
        if (e.key === 'Home') {
          this.mediaTrackPrevious();
        }
        if (e.key === 'End') {
          this.mediaTrackNext();
        }
      })

    this.cancelled = !this.autoplay;

    // Install built-in polyfills to patch browser incompatibilities.
    shaka.polyfill.installAll();

    // Check to see if the browser supports the basic APIs Shaka needs.
    if (shaka.Player.isBrowserSupported()) {
      // Everything looks good!
      this.videoElement = this.videoElementRef.nativeElement;
      this.videoUIElement = this.videoUIElementRef.nativeElement;

      this.initPlayer();
    } else {
      // This browser does not have the minimum set of APIs we need.
      console.error('Browser not supported!');
    }
  }

  ngOnDestroy(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.endOfVideoCounterId) {
      clearInterval(this.endOfVideoCounterId);
    }
    this.clearOverlayTimeout();
    this.clearPlayFocusTimeout();
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.destroy$.next();
  }

  getWebOSVersion(): string {
    if (webOS) {
      let version;
      webOS.deviceInfo((device => {
        version = device.sdkVersion?.split('.')[0];
      }));
      return version;
    }
    return null;
  }

  tizenNetworking() {
    try {
      if (webapis !== undefined && webapis !== null) {
        try {
          webapis.network.addNetworkStateChangeListener((value) => {
            if (value == webapis.network.NetworkState.GATEWAY_CONNECTED) {
              this.player.retryStreaming();
            }
          });

        } catch {
        }
        return;
      }
    } catch {
    }
  }

  private async initPlayer(): Promise<void> {

    let volume = +localStorage.getItem('volume');
    if (volume) {
      this.videoElement.volume = volume;
    }

    const localPlayer = new shaka.Player();

    localPlayer.addEventListener('error', (event) => {
      this.onError(event.detail);
    });

    // 2) Attach our <video> element
    await localPlayer.attach(this.videoElement);

    this.videoElement.addEventListener('error', () => {
      const me = this.videoElement.error!;
      console.error('Native <video> error:', me);
      this.onError({
        code: shaka.util.Error.Code.VIDEO_ERROR,
        data: [me.code, (me as any).extendedCode || null, (me as any).message || 'Unknown']
      });
    });

    const ui = new shaka.ui.Overlay(localPlayer, this.videoUIElement, this.videoElement);

    const controls = ui.getControls();

    this.player = localPlayer;

    ui.configure({
      'enableKeyboardPlaybackControls': false,
      'seekBarColors': {
        base: 'rgba(255, 255, 255, 0.3)',
        buffered: 'var(--player_buffered)',
        played: 'var(--player_played)',
      },
      'controlPanelElements': [
        'time_and_duration',
        'spacer',
      ],
      'addBigPlayButton': false
    });

    controls.addEventListener('caststatuschanged', this.onCastStatusChanged);

    this.configurePlayer();

    fromEvent(this.videoElement, 'canplay').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.videoLoaded = true;
      if (this.isPlaying) {
        this.videoElement.play();
      }
    });

    fromEvent(this.videoElement, 'seeking').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.seeking = true;
    });

    fromEvent(this.videoElement, 'seeked').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.seeking = false;
      if (this.isPlaying) {
        this.videoElement.play();
      }
    });

    this.player.addEventListener('error', (e) => {
      this.onError(e.detail);
    });
    this.player.addEventListener('adaptation', () => {
      this.player.getVariantTracks().forEach(function (track) {
        if (track.active) {
          let autoElement = document.getElementsByClassName('shaka-resolution-button')[0].children[1].children[1];
          if (autoElement) {
            autoElement.innerHTML = 'Auto (' + track.height + 'p)';
          }
        }
      });
    });

    this.videoElement.addEventListener('volumechange', () => {
      localStorage.setItem('volume', this.videoElement.volume + '');
    });

    this.videoElement.addEventListener('ended', () => {
      this.endOfVideo = true;
      if (!this.cancelled) {
        this.endOfVideoCounterId = setInterval(() => {
          this.endOfVideoTime = this.endOfVideoTime - 1;
          if (this.endOfVideoTime < 0) {
            if (!this.cancelled) {
              if (this.nextVideoId) {
                this.playNextVideo();
              }
            }
            clearInterval(this.endOfVideoCounterId);
          }
        }, 1000);
      }
    }, {once: true});

    this.videoElement.addEventListener('timeupdate', () => {
      let time = this.videoElement.currentTime;
      let intTime = (time | 0);
      if (time > 0 && intTime % this.frequency == 0 && intTime > this.lastReportedTime) {
        this.lastReportedTime = intTime;
        this.videoService.reportStreamProgress(
          this.uid,
          this.videoId,
          new StreamReport(time * 1000, this.videoElement.duration * 1000, this.durationWatchedSecondMs)
        ).subscribe();
      }
      this.timeLeft = this.videoElement.duration - time;
    });

    this.videoElement.addEventListener('playing', () => {
      this.isPlaying = true;
    });

    this.videoElement.addEventListener('pause', () => {
      this.isPlaying = false;
    });

    this.player.addEventListener('textchanged', () => {
      const lang = this.player.getTextTracks().find(x => x.active)?.language ?? 'null';
      this.storage.setString('caption-language', lang);
    });

    this.player.addEventListener('texttrackvisibility', () => {
      const currentLang = this.storage.getString('caption-language');
      const tracks = this.player.getTextTracks();
      if (currentLang !== null && currentLang !== undefined && currentLang !== 'null') {
        if (tracks.findIndex(x => x.language === currentLang) < 0 || tracks.length === 0) {
          return;
        }
      }

      const visible = this.player.getTextTracks().findIndex(x => x.active) > -1;
      if (!visible) {
        this.storage.setString('caption-language', null);
      }

      this.initCheckCaptionImage();
    });

    if (this.url === undefined) {
      this.url = this.fpsHlsUrl;
    }

    const webOsMajorVersion = this.getWebOSVersion();
    if (webOsMajorVersion) {
      const restrict = +webOsMajorVersion < 5;
      if (restrict) {
        this.url += '?r=true'
      }
    }

    try {
      await localPlayer.load(this.url);
      await this.addTextTracks();

      const captionLang = this.storage.getString('caption-language');
      if (captionLang && captionLang !== 'null') {
        const track = localPlayer.getTextTracks().find(t => t.language === captionLang);
        if (track) {
          localPlayer.setTextTrackVisibility(true);
          localPlayer.selectTextTrack(track);
        }
      }
      this.initCheckCaptionImage();

      if (this.startTimeMs > 0) {
        let startTimeS = this.startTimeMs / 1000;
        if (this.videoElement.duration - startTimeS < 30) {
          startTimeS = 0;
        }
        if (startTimeS < 10) {
          startTimeS = 0;
        }
        this.videoElement.currentTime = startTimeS;
      }

      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'shown') {
            if (!this.shakaControlsContainer.hasAttribute('shown')) {
              this.resetPlayFocus();
            }
          }
        });
      });

      this.mutationObserver.observe(this.shakaControlsContainer, {
        attributes: true //configure it to listen to attribute changes
      })
    } catch (loadErr) {
      console.error('Shaka load() failed:', loadErr);
      await this.player.destroy();
      this.rebuildVideoElement();
      this.retryCount = 0;
      return this.initPlayer();
    }
  }

  async addTextTracks() {
    for (const lang of Object.keys(this.textTracks)) {
      await this.player.addTextTrackAsync(this.textTracks[lang], lang, 'subtitle', 'text/vtt');
    }
  }

  toggleCaptions() {
    const textTracks = this.player.getTextTracks();
    const showingTracks = textTracks.filter(track => track.active);
    const active = !showingTracks.length;
    this.player.setTextTrackVisibility(active);
    this.captionsOn = active;
  }

  initCheckCaptionImage() {
    const textTracks = this.player.getTextTracks();
    const showingTracks = textTracks.filter(track => track.active);
    const active = showingTracks.length;
    this.captionsOn = active;
  }

  resetPlayFocus() {
    this.clearPlayFocusTimeout();
    this.playFocusTimeout = setTimeout(() => {
      this.playButton.nativeElement.focus();
    }, 400);
  }

  // async addTextTracks() {
  // 	for (const lang of Object.keys(this.textTracks)) {
  // 		await this.player.addTextTrackAsync(this.textTracks[lang], lang, 'subtitle', 'text/vtt');
  // 	}
  // }

  playNextVideo() {
    this.playVideo(this.nextVideoId);
  }

  playVideo(id: string) {
    const seasonId = this.route.snapshot.queryParamMap.get('seasonId');
    const watchlist = this.route.snapshot.queryParamMap.get('watchlist');
    const order = this.route.snapshot.queryParamMap.get('order');
    if (seasonId && watchlist) {
      this.router.navigate(['/video', id], {queryParams: {seasonId, watchlist, order}});
    } else if (watchlist) {
      this.router.navigate(['/video', id], {queryParams: {watchlist}});
    } else if (seasonId) {
      this.router.navigate(['/video', id], {queryParams: {seasonId, order}});
    } else {
      this.router.navigate(['/video', id]);
    }
  }

  cancelNextVideoTimer() {
    clearInterval(this.endOfVideoCounterId);
    this.endOfVideoTime = -1;
    this.cancelled = true;
    setTimeout(() => {
      this.playNext.nativeElement.focus();
    }, 150);
  }

  togglePausePlay() {
    if (this.videoElement.paused) {
      this.play();
    } else {
      this.pause()
    }

    this.addShownAttribute();
  }

  play() {
    this.clearOverlayTimeout();
    this.clearPlayFocusTimeout();
    this.videoElement.play();

    this.addShownAttribute();
  }

  pause() {
    this.videoElement.pause();
    this.addShownAttribute();
  }

  clearOverlayTimeout() {
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
      this.overlayTimeout = undefined;
    }
  }

  addShownAttribute() {
    const ele = this.shakaControlsContainer;
    if (ele) {
      ele.setAttribute('shown', 'true');
    }
  }

  removeShownAttribute() {
    if (!this.isPlaying) {
      return;
    }

    const ele = this.shakaControlsContainer;
    if (ele) {
      ele.removeAttribute('shown');
    }
  }

  private clearPlayFocusTimeout() {
    if (this.playFocusTimeout) {
      clearTimeout(this.playFocusTimeout);
      this.playFocusTimeout = undefined;
    }
  }

  private onCastStatusChanged(event) {
    const newCastStatus = event['newStatus'];
    if (newCastStatus) {
      // Handle cast status change
      console.log('The new cast status is: ' + newCastStatus);
    }
  }

  private retryCount = 0;
  private readonly maxRetries = 5;

  private async onError(error: {
    code: number;
    data: any[];
  }): Promise<void> {
    console.log('Shaka/onError got:', error.code, error.data);

    const Code = shaka.util.Error.Code;
    const isMsoFail = error.code === Code.MEDIA_SOURCE_OPERATION_FAILED; // 3014
    const isMseThrow = error.code === Code.MEDIA_SOURCE_OPERATION_THREW;   // 3015
    const isVideoErr = error.code === Code.VIDEO_ERROR;                   // 3016

    if (isVideoErr &&
      typeof error.data[2] === 'string' &&
      error.data[2].includes('PIPELINE_ERROR_DISCONNECTED')) {
      console.log('Detected MediaFoundationRendererClient disconnect → full rebuild');
      await this.player.destroy();
      this.rebuildVideoElement();
      this.retryCount = 0;
      return this.initPlayer();
    }

    if ((isMsoFail || isMseThrow || isVideoErr) && this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`Transient Shaka error ${error.code}, retry #${this.retryCount}`);
      try {
        await this.player.retryStreaming();
        console.info('retryStreaming() succeeded');
        return;
      } catch (retryErr) {
        console.error('retryStreaming() failed', retryErr);
      }
    }

    if (isMsoFail || isMseThrow) {
      console.log('Persistent MSE failure → full rebuild');
      await this.player.destroy();
      this.rebuildVideoElement();
      this.retryCount = 0;
      return this.initPlayer();
    }

    if (isVideoErr && error.data[0] === this.videoElement.error.MEDIA_ERR_DECODE) {
      console.log('Persistent decode error → full rebuild');
      await this.player.destroy();
      this.rebuildVideoElement();
      this.retryCount = 0;
      return this.initPlayer();
    }

    console.log('Fatal Shaka error, cannot recover:', error);
  }

  private rebuildVideoElement(): void {
    const oldVideo = this.videoElement;
    const parent = oldVideo.parentElement!;
    const idx = Array.prototype.indexOf.call(parent.children, oldVideo);

    const newVideo = document.createElement('video');
    newVideo.id = oldVideo.id;
    newVideo.autoplay = oldVideo.autoplay;
    newVideo.poster = oldVideo.poster;
    newVideo.style.cssText = oldVideo.style.cssText;
    newVideo.setAttribute('data-shaka-player', '');

    parent.removeChild(oldVideo);
    parent.insertBefore(newVideo, parent.children[idx] || null);

    this.videoElement = newVideo;
    this.videoElementRef.nativeElement = newVideo;
  }

  onResize() {
    this.configurePlayer();
  }

  configurePlayer() {
    let height = this.videoUIElement.getBoundingClientRect().height;
    let width = this.videoUIElement.getBoundingClientRect().width;
    // if (height == 0 && width > 0) {
    // 	height = width / 16 * 9;
    // } else if (height == 0) {
    // 	height = 8000;
    // } else {
    // }
    // if (height < 1080) {
    // 	height = 1080;
    // }

    // height = 8000;

    this.player.getNetworkingEngine().registerResponseFilter((type, response) => {
      if (type != shaka.net.NetworkingEngine.RequestType.LICENSE) {
        return;
      }

      if (this.fairplayUrl) {
        console.log('FairPlay Detected');
        let responseText = shaka.util.StringUtils.fromUTF8(response.data);
        // Trim whitespace.
        responseText = responseText.trim();

        console.log('Decoding base64 license response');
        // Decode the base64-encoded data into the format the browser expects.
        response.data = shaka.util.Uint8ArrayUtils.fromBase64(responseText).buffer;
      }
    });

    this.player.configure({
      drm: {
        servers: {
          'com.widevine.alpha': this.widevineUrl,
          'com.microsoft.playready': this.widevineUrl?.replace('widevine', 'playready')
        },
        advanced: {
          'com.widevine.alpha': {
            'videoRobustness': 'SW_SECURE_CRYPTO',
            'audioRobustness': 'SW_SECURE_CRYPTO'
          },
          'com.microsoft.playready': {
            'videoRobustness': 'SW_SECURE_DECODE',
            'audioRobustness': 'SW_SECURE_DECODE',
          },
        }
      },
      restrictions: {
        maxHeight: height + 120
      }
    });
  }

  @HostListener('click', ['$event'])
  onPlayerClicked() {
    this.playButton?.nativeElement.focus();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: any) {
    console.log("keyPressed", event.keyCode);
    const key = this.checkXboxKey(event);
    switch (key) {
      case 'ArrowLeft':
        this.showTempOverlay();
      case 'ArrowRight':
        this.showTempOverlay();
      case 'ArrowUp':
        this.showTempOverlay();
      case 'ArrowDown':
        this.showTempOverlay();
      case 'Enter':
        this.showTempOverlay();
      default:
        this.showTempOverlay();
    }

    switch (event.keyCode) {
      case 177:
        this.skipBackward(null);
        return;
      case 176:
        this.skipForward(null);
        return;
      case 179:
        this.togglePausePlay();
        return;
    }

    try {
      if (tizen !== undefined && tizen !== null) {
        switch (event.keyCode) {
          case tizen.tvinputdevice.getKey('MediaPlayPause').code:
            this.togglePausePlay();
            break;
          case tizen.tvinputdevice.getKey('MediaPlay').code:
            this.play();
            break;
          case tizen.tvinputdevice.getKey('MediaPause').code:
            this.pause();
            break;
          case tizen.tvinputdevice.getKey('MediaStop').code:
            this.handleBackKey();
            break;
          case tizen.tvinputdevice.getKey('MediaRewind').code:
            this.skipBackward(null);
            break;

          case tizen.tvinputdevice.getKey('MediaFastForward').code:
            this.skipForward(null);
            break;
          case tizen.tvinputdevice.getKey('MediaTrackPrevious').code:
            this.mediaTrackPrevious();
            break;
          case tizen.tvinputdevice.getKey('MediaTrackNext').code:
            this.mediaTrackNext();
            break;
          default:
            break;
        }
      }
    } catch {
    }
  }

  mediaTrackPrevious() {
    if (this.previousVideoId) {
      this.playVideo(this.previousVideoId);
    }
  }

  mediaTrackNext() {
    if (this.nextVideoId) {
      this.playVideo(this.nextVideoId)
    }
  }

  showTempOverlay() {
    this.clearOverlayTimeout();
    this.clearPlayFocusTimeout();

    if (!this.isPlaying) {
      return;
    }

    this.addShownAttribute()
    this.overlayTimeout = setTimeout(() => {
      this.removeShownAttribute();
      this.resetPlayFocus();
    }, this.overlayTimeoutDelay);
  }

  shown() {
    const ele = this.shakaControlsContainer;
    if (ele) {
      return ele.hasAttribute('shown');
    }
    return false;
  }

  skipForward(event) {
    if (event) {
      event.stopPropagation();
    }
    let time = this.videoElement.currentTime;
    time += 10;

    if (time < this.videoElement.duration) {
      this.videoElement.currentTime = time;
    }
  }

  skipBackward(event) {
    if (event) {
      event.stopPropagation();
    }
    let time = this.videoElement.currentTime;
    time -= 10;

    if (time < 0) {
      time = 0;
    }
    this.videoElement.currentTime = time;
  }

  returnToPreviousPage() {
    this.router.navigateByUrl(this.app.previousUrl);
  }

  watchAgain() {
    const id = this.route.snapshot.paramMap.get('id');
    const seasonId = this.route.snapshot.queryParamMap.get('seasonId');
    const watchlist = this.route.snapshot.queryParamMap.get('watchlist');
    const order = this.route.snapshot.queryParamMap.get('order');

    if (this.endOfVideoCounterId) {
      clearInterval(this.endOfVideoCounterId);
    }

    this.router.navigate(['/blank'], {skipLocationChange: true}).then(() => {
      if (seasonId && watchlist) {
        this.router.navigate(['/video', id], {queryParams: {seasonId, watchlist, order, t: 0}});
      } else if (watchlist) {
        this.router.navigate(['/video', id], {queryParams: {watchlist, t: 0}});
      } else if (seasonId) {
        this.router.navigate(['/video', id], {queryParams: {seasonId, order, t: 0}});
      } else {
        this.router.navigate(['/video', id], {queryParams: {t: 0}});
      }
    });
  }

  focusElement = () => {
    this.playButton.nativeElement.focus();
  }


  closeReportMediaModal() {
    this.showReportMediaModal = false;
    this.videoElement.play();
  }

  modal() {
    this.videoElement.pause();
    this.showReportMediaModal = true;
  }

  private handleBackKey() {
    if (this.showReportMediaModal) {
      this.closeReportMediaModal();
      return;
    }
    this.pause();
    this.returnToPreviousPage();
  }

  @HostListener('document:keydown', ['$event'])
  handleRemoteEvent(event: KeyboardEvent) {
    switch (event.keyCode) {
      case 415: // Play
        this.play();
        break;
      case 19: // Pause
        this.togglePausePlay();
        break;
      case 412: // Rewind
        this.skipBackward(event); // rewind 10 seconds
        break;
      case 417: // Fast Forward
        this.skipForward(event); // fast forward 10 seconds
        break;
      default:
        break;
    }
  }

  addToPlaylist(media: any) {
    if (!this.addToPlaylistLoading) {
      this.addToPlaylistLoading = true;
      this.playlistService.addToPlaylist(media).subscribe({
        next: async () => {
          this.completeWatchlistToggle();
        },
        error: () => {
          this.completeWatchlistToggle();
        }
      });
    }
  }

  deleteFromPlaylist(media: any) {
    if (!this.addToPlaylistLoading) {
      this.addToPlaylistLoading = true;
      this.playlistService.deleteFromPlaylist(media).subscribe({
        next: async () => {
          this.completeWatchlistToggle();
        },
        error: () => {
          this.completeWatchlistToggle();
        }
      });
    }
  }

  completeWatchlistToggle() {
    this.addToPlaylistLoading = false;
    setTimeout(() => {
      this.watchlistButton.nativeElement.focus();
    }, 50);
  }

  existsInPlaylist(mediaId: number) {
    return this.playlistService.hasMediaId(mediaId);
  }

  checkXboxKey(event: KeyboardEvent): string {
    let key = event.key;
    if (key === 'Unidentified') {
      const xboxKeyMap: Record<number, string> = {
        37: 'ArrowLeft',
        205: 'ArrowLeft',

        206: 'ArrowRight',
        39: 'ArrowRight',

        203: 'ArrowUp',
        38: 'ArrowUp',

        204: 'ArrowDown',
        40: 'ArrowDown',

        195: 'Enter',

        27: 'BackspaceXbox'
      };

      key = xboxKeyMap[event.keyCode] || xboxKeyMap[event.which] || key;
    }
    return key;
  }
}
